"use client";

import React, { useEffect, useState } from "react";
import { ANIMALIA_TOKEN_VALUES, TOKEN_REGISTRY, type AniTheme } from "./tokens";
import AniButton from "./primitives/AniButton";
import AniIcon from "./primitives/AniIcon";

export const XRAY_MODES = ["Structure", "Spacing", "Tokens", "Components"] as const;
export type XrayMode = (typeof XRAY_MODES)[number];
type Kind = "component" | "structure" | "spacing";
type Inspection = {
  id: string; name: string; parent: string | null; depth: number; kind: Kind;
  x: number; y: number; width: number; height: number;
  padding: number[]; margin: number[]; rowGap: number; columnGap: number; tokens: string[];
};
const selectors: Record<Kind, string> = {
  component: "[data-token]", structure: "[data-structure]", spacing: "[data-pad], [data-gap]",
};
const kindFor = (mode: XrayMode): Kind => mode === "Structure" ? "structure" : mode === "Spacing" ? "spacing" : "component";
const px = (value: string) => Math.round((Number.parseFloat(value) || 0) * 10) / 10;

/** Observe the real elements; the overlay never intercepts their interactions. */
export function useXrayInspection(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean, mode: XrayMode) {
  const [items, setItems] = useState<Inspection[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || !enabled) return;
    let frame = 0;
    let disposed = false;
    const nodes = (kind: Kind) => kind === "structure"
      ? [root, ...root.querySelectorAll<HTMLElement>(selectors[kind])]
      : [...root.querySelectorAll<HTMLElement>(selectors[kind])];
    const read = () => {
      const bounds = root.getBoundingClientRect();
      const next = (["component", "structure", "spacing"] as const).flatMap(kind => nodes(kind).map((node, index) => {
        const rect = node.getBoundingClientRect();
        const style = getComputedStyle(node);
        let ancestor = node.parentElement?.closest<HTMLElement>("[data-token]");
        const parent = ancestor && root.contains(ancestor) ? ancestor.dataset.token ?? null : null;
        let depth = 0;
        while (ancestor && root.contains(ancestor)) {
          depth++;
          ancestor = ancestor.parentElement?.closest<HTMLElement>("[data-token]");
        }
        const name = node.dataset.token ?? node.dataset.structure ?? "Container";
        const explicitTokens = (node.dataset.inspectTokens ?? "").split(" ").filter(Boolean);
        const matchingTokens = TOKEN_REGISTRY.flatMap(group => group.tokens)
          .filter(token => token.figma.startsWith(name.split("/")[0].replace(/ \(.*\)/, "")))
          .map(token => token.cssVar);
        return {
          id: `${kind}-${index}`, name, parent, depth, kind,
          x: rect.left - bounds.left, y: rect.top - bounds.top, width: rect.width, height: rect.height,
          padding: [style.paddingTop, style.paddingRight, style.paddingBottom, style.paddingLeft].map(px),
          margin: [style.marginTop, style.marginRight, style.marginBottom, style.marginLeft].map(px),
          rowGap: px(style.rowGap), columnGap: px(style.columnGap),
          tokens: explicitTokens.length ? explicitTokens : matchingTokens,
        };
      })).filter(item => item.width > 0 && item.height > 0);
      setItems(current => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => { if (!disposed) { cancelAnimationFrame(frame); frame = requestAnimationFrame(read); } };
    const inspect = (event: Event) => {
      const kind = kindFor(mode);
      const node = event.target instanceof Element ? event.target.closest<HTMLElement>(selectors[kind]) : null;
      const index = node ? nodes(kind).indexOf(node) : -1;
      setSelected(index >= 0 ? `${kind}-${index}` : null);
    };
    const clear = () => setSelected(null);
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") clear(); };
    clear();
    const observer = new ResizeObserver(schedule);
    [root, ...root.querySelectorAll<HTMLElement>(Object.values(selectors).join(","))].forEach(node => observer.observe(node));
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, {subtree:true, childList:true, attributes:true, attributeFilter:["style", "class", "hidden"]});
    root.addEventListener("pointerover", inspect);
    root.addEventListener("pointerleave", clear);
    root.addEventListener("focusin", inspect);
    root.addEventListener("focusout", clear);
    root.addEventListener("keydown", keydown);
    window.addEventListener("resize", schedule);
    document.fonts.ready.then(schedule);
    schedule();
    return () => {
      disposed = true; observer.disconnect(); mutations.disconnect(); cancelAnimationFrame(frame);
      root.removeEventListener("pointerover", inspect); root.removeEventListener("pointerleave", clear);
      root.removeEventListener("focusin", inspect); root.removeEventListener("focusout", clear);
      root.removeEventListener("keydown", keydown); window.removeEventListener("resize", schedule);
    };
  }, [enabled, mode, ref]);
  return { items, selected };
}

export function XrayToolbar({ mode, onMode, panelId, onExit }: {
  mode: XrayMode; onMode: (mode: XrayMode) => void; panelId: string; onExit: () => void;
}) {
  return <div className="ani-xray-toolbar-row">
    <div className="ani-xray-tabs" role="tablist" aria-label="X-ray inspection layers">
      {XRAY_MODES.map((entry, index) => <button key={entry} type="button" role="tab" id={`${panelId}-${entry}`}
        aria-selected={mode === entry} aria-controls={panelId} tabIndex={mode === entry ? 0 : -1}
        onClick={() => onMode(entry)} onKeyDown={event => {
          const next = event.key === "ArrowRight" ? (index + 1) % XRAY_MODES.length
            : event.key === "ArrowLeft" ? (index + XRAY_MODES.length - 1) % XRAY_MODES.length
            : event.key === "Home" ? 0 : event.key === "End" ? XRAY_MODES.length - 1 : null;
          if (next === null) return;
          event.preventDefault(); onMode(XRAY_MODES[next]);
          (event.currentTarget.parentElement?.children[next] as HTMLButtonElement)?.focus();
        }}>{entry}</button>)}
    </div>
    <AniButton variant="tertiary" iconOnly aria-pressed aria-label="Exit X-ray mode" onClick={onExit}>
      <AniIcon name="detective" />
    </AniButton>
  </div>;
}

function resolveToken(value: string, vars: Record<string, string>, visited = new Set<string>()): string {
  const alias = /^var\((--ani-[^)]+)\)$/.exec(value)?.[1];
  if (!alias || visited.has(alias) || !vars[alias]) return value;
  visited.add(alias);
  return resolveToken(vars[alias], vars, visited);
}

export function XrayOverlay({ items, mode, selected, theme }: {
  items: Inspection[]; mode: XrayMode; selected: string | null; theme: AniTheme;
}) {
  const visible = items.filter(item => item.kind === kindFor(mode));
  const width = items.find(item => item.id === "structure-0")?.width ?? ANIMALIA_TOKEN_VALUES.xrayTooltipMaxWidth;
  return <div className="ani-xray-overlay" aria-hidden="true">
    {visible.map(item => <div key={item.id}
      className={`ani-xray-boundary${item.id === selected ? " is-selected" : ""}${item.depth ? " is-nested" : ""}`}
      style={{left:item.x, top:item.y, width:item.width, height:item.height}}>
      {mode === "Spacing" && <div className="ani-xray-padding" style={{borderWidth:item.padding.map(n => `${n}px`).join(" ")}} />}
      {item.id === selected && <span className="ani-xray-badge" style={{left:Math.min(0, width - item.x - Math.min(ANIMALIA_TOKEN_VALUES.xrayTooltipMaxWidth, width))}}>
        <strong>{item.parent ? `${item.parent} › ` : ""}{item.name}</strong>
        {mode === "Structure" && <span>{Math.round(item.width)} × {Math.round(item.height)} px</span>}
        {mode === "Spacing" && <>
          <span>Padding · {item.padding.join(" / ")} px</span>
          <span>Gap · {item.rowGap} / {item.columnGap} px</span>
          <span>Margin · {item.margin.join(" / ")} px</span>
        </>}
        {mode === "Tokens" && <>
          {(item.tokens.length ? item.tokens : ["--ani-family-ui", "--ani-text", "--ani-space-sm"]).map(name => <span key={name}>
            {name.replace("--ani-", "")} · {resolveToken(theme.vars[name] ?? "—", theme.vars)}
          </span>)}
          <span>Font · {theme.fontLabel} · text contrast {theme.textContrast.toFixed(1)}:1</span>
        </>}
        {mode === "Components" && <span>{item.depth ? `Nested component · level ${item.depth}` : "Root component"}</span>}
      </span>}
    </div>)}
  </div>;
}
